import React, { useRef, useMemo } from 'react'
import { RenderItemProps } from './types'
import { View, Text } from '@/components'
import { formatDateLocalized, IS_SSR, variantProvider } from '@/app'
import { TextBubble } from '../atoms/TextBubble'
import { DebugItem } from './shared'

export const FeedText = (props: RenderItemProps) => {
  const { item } = props

  const promptDivRef = useRef<HTMLDivElement>(null)
  const answerDivRef = useRef<HTMLDivElement>(null)

  const isPromptAnswer = item.type === 'text_answer' && item?.prompt
  const creationDate = formatDateLocalized(new Date(item.created_datetime), 'learn.stories.date_format')

  const feedItemHeight = useMemo(() => {
    if (IS_SSR) return 0
    return (window.innerHeight - 120)
  }, [])

  const pushAmount = useMemo(() => {
    if (!isPromptAnswer || !promptDivRef.current || !answerDivRef.current) return 0

    const promptHeight = promptDivRef.current.getBoundingClientRect().height
    const answerHeight = answerDivRef.current.getBoundingClientRect().height
    const spacing = 48

    const overlayBottom = promptHeight + spacing
    const bubbleStart = (feedItemHeight - answerHeight) / 2
    const overlap = overlayBottom - bubbleStart
    return overlap > 0 ? overlayBottom : 0
  }, [promptDivRef.current, answerDivRef.current, isPromptAnswer])

  if (!item?.prompt && !item.post) return null

  return <>
    <DebugItem {...props} />

    {isPromptAnswer ? (
      <div ref={promptDivRef} style={styles.prompt}>
        <TextBubble
          title={item?.prompt?.title}
          description={item?.prompt?.description}
          icon={item.type === 'text' ? 'text' : undefined}
        />
      </div>
    ) : null}

    <View variants={['justifyCenter', 'paddingHorizontal:2', 'fullWidth', 'column', 'flex', 'gap:2']} style={{ overflowY: 'auto', zIndex: 1 }}>
      <div ref={answerDivRef} style={{ marginTop: pushAmount }}>
        <TextBubble
          header={<Text text={creationDate} variants={['p4', 'marginBottom:1']} />}
          title={item?.post?.title}
          description={item?.post?.description}
          lesson={item?.post?.lesson}
          icon={item.type === 'text' ? 'text' : 'clipboard-pen'}
        />
      </div>

      {isPromptAnswer ? <div /> : null}
    </View>
  </>
}

const styles = variantProvider.createComponentStyle(theme => ({
  prompt: {
    ...theme.presets.absolute,
    paddingTop: theme.spacing.value(2),
    top: 0,
    left: theme.spacing.value(2),
    right: theme.spacing.value(2),
    zIndex: 1,
  }
}), true)